import { Checkbox as BaseCheckbox } from "@base-ui/react/checkbox";
import type React from "react";

// Owned checkbox primitive.
//
// Only components/primitives may import @base-ui/react; see importBoundary.test.ts
// and docs/ui-boundary-migration-plan.md.
//
// The public API exposes values, never library event shapes. Base UI's
// onCheckedChange takes a second `eventDetails` argument carrying its own reason
// vocabulary and cancellation API; forwarding it would leak the implementation
// detail this boundary exists to contain, so it is dropped.
//
// DOM shape, verified against @base-ui/react@1.6.0 source rather than inferred:
// CheckboxRoot renders a `<span role="checkbox" aria-checked>` carrying focus and
// key handling, followed by a sibling `<input type="checkbox" aria-hidden
// tabindex="-1">` that exists only to carry the value. This is the mirror image
// of the Slider in Wave 2, where semantics moved *onto* a native input. Either
// way the lesson is the same: resolve the control by role and accessible state,
// never by tag name or by a raw ARIA attribute selector.
//
// The replaced markup was a native `<input type="checkbox">` visually hidden by
// `.control input { opacity: 0 }` with a sibling `<span class="control_indicator">`
// drawing the box and a `::after` drawing the tick. The appearance is reproduced
// in styles/components/_checkbox.scss against the Root and Indicator slots, so no
// rule selects Base UI's incidental nesting.
//
// `indeterminate` is deliberately not exposed. No in-scope call site uses a mixed
// state, and the legacy stylesheet has no mixed-state appearance to reproduce --
// its tick is binary -- so exposing it would ship a silently unstyled state.

export type CheckboxProps = {
  /** Controlled state. Use `defaultChecked` for an uncontrolled checkbox. */
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  name?: string;
  /**
   * Applied to the hidden input, which is what a wrapping or `htmlFor` label
   * associates with. Base UI puts the id there rather than on the Root span, and
   * then mirrors the label back onto the span as `aria-labelledby`, so the
   * accessible name resolves for the element that actually has the role.
   */
  id?: string;
  /** Accessible name for the rare call site that renders no visible label text. */
  label?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
  "aria-required"?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  onBlur?: React.FocusEventHandler<HTMLElement>;
  onFocus?: React.FocusEventHandler<HTMLElement>;
};

export default function Checkbox({
  checked,
  defaultChecked,
  disabled,
  id,
  label,
  name,
  onBlur,
  onCheckedChange,
  onFocus,
  readOnly,
  required,
  ...aria
}: CheckboxProps): React.ReactElement {
  return (
    <BaseCheckbox.Root
      aria-describedby={aria["aria-describedby"]}
      aria-invalid={aria["aria-invalid"]}
      aria-label={label}
      aria-required={aria["aria-required"]}
      checked={checked}
      data-slot="checkbox-root"
      defaultChecked={defaultChecked}
      disabled={disabled}
      id={id}
      name={name}
      onBlur={onBlur}
      onCheckedChange={onCheckedChange && ((next) => onCheckedChange(next))}
      onFocus={onFocus}
      readOnly={readOnly}
      required={required}
    >
      {/* Kept mounted so the tick is shown and hidden by `display`, exactly as
          the replaced `.control_indicator::after` was. Base UI's default is to
          unmount it, which would make the tick's presence depend on transition
          bookkeeping rather than on state. */}
      <BaseCheckbox.Indicator data-slot="checkbox-indicator" keepMounted />
    </BaseCheckbox.Root>
  );
}
